import React, { useState, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AuthContext, MediaQueriesContext } from "../../App";
import { useApiQuery, useApiMutation } from "../../hooks";
import qs from "query-string";
import cx from "classnames";

import Button from "Components/button/Button";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import TranslatedString from "Components/TranslatedString";
import ConfirmModal from "Components/confirmModal/ConfirmModal";
import { Icon, plus, globe, addDomain } from "Icons/index";
import DomainRow from "Components/domainRow/DomainRow";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import Modal from "Components/smallModal/smallModal";

import s from "./listDomains.scss";
import { t } from "i18next";

const DOMAIN_STATUSES = ["activating", "disabling", "enabling", "deleting", "updating"];

const DomainsListScreen = () => {
  const history = useHistory();
  const location = useLocation();
  const { isLoggedUserAdmin, getLoggedUser } = useContext(AuthContext);
  const { isMediaMobile, isMobileDevice } = useContext(MediaQueriesContext);
  const query = qs.parse(location.search);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState({});
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);

  const { mutate: deleteDomain } = useApiMutation("deleteDomain", {
    optimistic: (domainCode) =>
      updateDomains((domains) => domains.map((d) => (d.code === domainCode ? { ...d, status: "deleting" } : d))),
  });
  const { mutate: changeDomainStatus } = useApiMutation("changeDomainStatus", {
    optimistic: (domainCode) =>
      updateDomains((domains) => domains.map((d) => (d.code === domainCode ? { ...d, status: "updating" } : d))),
  });

  const {
    data: domains,
    isLoading: areDomainsLoading,
    isRefreshing: domainsRefreshing,
    pagination: paginationInfo,
    getData: getDomains,
    pagination: domainsPaginationInfo,
    updateData: updateDomains,
  } = useApiQuery("getDomains", {
    enablePagination: true,
    autoRefresh: {
      shouldRefresh: (domains) => {
        const enqueued = domains.filter(
          (d) =>
            DOMAIN_STATUSES.includes(d.status) || (d.status === "ownership_check" && d.status_detail === "verifying")
        );
        return enqueued.length > 0 && !areDomainsLoading;
      },
      interval: 4000,
    },
  });

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setSelectedDomain({});
  };

  const handleDeleteModalOpen = (domain) => {
    if (domain.archive) {
      setIsArchiveModalOpen(true);
    } else {
      setIsDeleteModalOpen(true);
      setSelectedDomain(domain);
    }
  };

  const handleArchiveModalClose = () => {
    setIsArchiveModalOpen(false);
  };

  const handleDomainDelete = async () => {
    const { code } = selectedDomain;
    try {
      await deleteDomain(code);
      handleDeleteModalClose();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDomainStatusChange = async (code, status) => {
    try {
      await changeDomainStatus(code, status);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddDomain = () => {
    history.push({
      pathname: "/domains/new",
      state: {
        prevPagination: location.search,
      },
    });
  };

  const loggedUser = getLoggedUser();
  const canAddDomain = !(loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("create_domains"));

  const filters =
    loggedUser.type === "Admin"
      ? ["all", "enabled", "disabled", "failure", "ownership_check", "unhealthy"]
      : ["all", "enabled", "disabled", "ownership_check", "unhealthy"];

  const tableHeaders = [
    {
      name: t("table.header.status"),
      size: isMobileDevice ? "2rem" : "4rem",
      center: true,
    },
    {
      name: t("table.header.domain"),
      key: "name",
      sortable: true,
      activeSorting: query.sort_field === "name" && query.sort,
    },
    {
      name: t("table.header.company"),
      size: "10rem",
      hideOnMobile: true,
      sortable: true,
      key: "company",
      activeSorting: query.sort_field === "company" && query.sort,
    },
    {
      name: t("table.header.customer"),
      size: "10rem",
      hide: !isLoggedUserAdmin(),
      hideOnMobile: true,
    },
    {
      name: t("table.header.accounts"),
      size: "5rem",
      hideOnMobile: true,
      sortable: true,
      key: "no_email_accounts",
      activeSorting: query.sort_field === "no_email_accounts" && query.sort,
    },
    {
      name: t("table.header.alias.domain"),
      size: "4.5rem",
      hideOnMobile: true,
      sortable: true,
      key: "no_alias_domains",
      activeSorting: query.sort_field === "no_alias_domains" && query.sort,
    },
    {
      name: t("table.header.alias.email"),
      size: "4.5rem",
      hideOnMobile: true,
      sortable: true,
      key: "no_alias_email_accounts",
      activeSorting: query.sort_field === "no_alias_email_accounts" && query.sort,
    },
    { size: "3rem", center: true, hideOnMobile: true },
    { size: "3rem", center: true, hideOnMobile: true },
    { size: "3rem", center: true },
  ];

  return (
    <div className="contentWrapper">
      <div className={s.listDomains}>
        <div className={s.headerList}>
          <div className={cx("sectionTitle", s.titleList)}>
            <TranslatedString>domains.list.header.title</TranslatedString>
          </div>

          {!isMediaMobile && (
            <div className={cx("sectionDesc", s.descList)}>
              <TranslatedString>domains.list.header.dsc</TranslatedString>
            </div>
          )}
          <div className={s.actionsList}>
            <Button docsKey="domains" color="docs" className={s.btnList} onlyIcon={isMediaMobile}>
              button.docs
            </Button>
            {canAddDomain && (
              <Button icon={plus} onClick={handleAddDomain} onlyIcon={isMediaMobile}>
                button.addDomain
              </Button>
            )}
          </div>
        </div>

        <div className={s.contentList}>
          <FullPaginationControls
            className={s.filters}
            paginationInfo={paginationInfo}
            location={location}
            history={history}
            refreshList={() => getDomains(qs.parse(location.search))}
            listFilters={filters}
            listFilterLegends={{
              all: <Icon svg={globe} width="0.75rem" height="0.75rem" />,
              enabled: <DomainStatusBadge status="enabled" isHealthy showAsPreview />,
              disabled: <DomainStatusBadge status="disabled" isHealthy showAsPreview />,
              failure: loggedUser.type === "Admin" && <DomainStatusBadge status="failure" isHealthy showAsPreview />,
              ownership_check: <DomainStatusBadge status="ownership_check" isHealthy showAsPreview />,
              unhealthy: <DomainStatusBadge status="enabled" showAsPreview />,
            }}
            showCustomerSelection={isLoggedUserAdmin()}
            isLoading={areDomainsLoading}
            totals={domainsPaginationInfo.totals}
          />

          <div className={s.table}>
            <ResponsiveTable
              history={history}
              areRowsLoading={areDomainsLoading && !domainsRefreshing}
              location={location}
              addIcon={addDomain}
              addMsg={t("table.row.add.msg.domain")}
              addDesc={t("table.row.add.desc.domain")}
              headers={tableHeaders}
              rows={domains.map((dom) => (
                <DomainRow
                  key={dom.code}
                  location={location}
                  domain={dom}
                  isLoggedUserAdmin={isLoggedUserAdmin()}
                  history={history}
                  disableDomain={(code) => handleDomainStatusChange(code, "disabling")}
                  deleteDomain={handleDeleteModalOpen}
                  changeDomainStatus={handleDomainStatusChange}
                />
              ))}
            />
          </div>
        </div>
      </div>

      <ConfirmModal
        title={t("confirmationModal.title.delte.domain")}
        text={
          <div>
            <TranslatedString subs={{ domainName: selectedDomain.name }}>
              confirmationModal.text.delete.domains
            </TranslatedString>
            <br />
            {loggedUser.orders?.[0]?.billing_cycle === "annually" &&
              "Questo dominio fa parte di un ordine annuale. Cancellandolo non verrà rimosso dall'ordine! Per evitare che venga rinnovato alla prossima fattura, vai nella sezione ordini e cancellalo manualmente."}
          </div>
        }
        textButton={t("button.delete")}
        opened={isDeleteModalOpen}
        action={handleDomainDelete}
        handleClose={handleDeleteModalClose}
      />

      <Modal
        className={s.modaNoAction}
        opened={isArchiveModalOpen}
        closeModal={handleArchiveModalClose}
        title={t("modal.noAction.title")}
      >
        <div>
          <TranslatedString>modal.withArchive.domain</TranslatedString>
        </div>
        <div className={s.footerModaNoAction}>
          <Button onClick={handleArchiveModalClose} type="button" color="outline">
            button.close
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default DomainsListScreen;
